// Lightweight global "your connection seems slow" signal — any part of the app
// (API calls, the video player, etc.) can report a slow-network symptom here,
// and NetworkStatusBanner (mounted once at the app root) shows a toast for it.
// Deliberately not a full network-quality monitor: we don't have a reliable way
// to measure "slow" in the abstract, so instead we react to concrete symptoms
// (a request taking too long, the video player stalling) as they happen.

type Listener = () => void;
const listeners = new Set<Listener>();

// Don't re-trigger the banner for every slow request in a row — once every
// COOLDOWN_MS is enough to inform the student without spamming toasts during a
// genuinely bad stretch of connectivity.
const COOLDOWN_MS = 20000;
let lastNotifiedAt = 0;

export function onSlowNetwork(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function reportSlowNetwork() {
  const now = Date.now();
  if (now - lastNotifiedAt < COOLDOWN_MS) return;
  lastNotifiedAt = now;
  listeners.forEach((l) => l());
}

// How long an API request has to take before we treat it as a "slow network"
// symptom rather than just a normal slow endpoint. Deliberately generous —
// this is meant to catch genuinely bad connections, not flag every heavy query.
export const SLOW_REQUEST_THRESHOLD_MS = 8000;

// Fully offline is a different (and more certain) signal than "slow" — the
// browser knows instantly via the online/offline events instead of having to
// wait for a request to time out, so it gets its own channel and its own
// persistent banner rather than the auto-hiding slow-network toast.
type OfflineListener = (isOffline: boolean) => void;
const offlineListeners = new Set<OfflineListener>();

export function onOfflineStatusChange(listener: OfflineListener): () => void {
  offlineListeners.add(listener);
  listener(!navigator.onLine); // report current state immediately on subscribe
  return () => offlineListeners.delete(listener);
}

if (typeof window !== "undefined") {
  window.addEventListener("offline", () => offlineListeners.forEach((l) => l(true)));
  window.addEventListener("online", () => offlineListeners.forEach((l) => l(false)));
}
